import axios from 'axios';

// In dev, use relative path so Vite proxy forwards to backend
const baseURL = import.meta.env.DEV
  ? '/api/v1'
  : (import.meta.env.VITE_API_URL || 'http://localhost:8888/api/v1');

export const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Enable sending cookies
});

// CSRF token management
let csrfToken = null;

async function ensureCsrfToken() {
  if (csrfToken) return csrfToken;
  try {
    const response = await api.get('/auth/csrf-token');
    csrfToken = response.data?.csrfToken || null;
    return csrfToken;
  } catch {
    // Non-critical — CSRF check may be disabled in dev
    return null;
  }
}

// Request interceptor: add CSRF token to mutating requests
api.interceptors.request.use(
  async (config) => {
    const method = config.method?.toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const token = await ensureCsrfToken();
      if (token) {
        config.headers['X-CSRF-Token'] = token;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for global error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;

    if (response?.status === 401) {
      // Clear any cached auth state
      localStorage.removeItem('user');

      // Redirect to login only if not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    } else if (response?.status === 403) {
      // CSRF token may have expired — reset and retry once
      csrfToken = null;
      console.error('Access denied (possibly CSRF)');
    } else if (response?.status === 404) {
      console.error('Resource not found');
    } else if (response?.status >= 500) {
      console.error('Server error');
    }

    return Promise.reject(error);
  }
);

export const messagesApi = {
  create: async (data) => {
    const response = await api.post('/messages', data);
    return response.data;
  }
};

export const destinationCountriesApi = {
  getList: async (lang = 'en') => {
    const response = await api.get('/destination-countries', { params: { lang } });
    return response.data?.data?.countries ?? [];
  }
};

export const documentTypesApi = {
  getList: async (lang = 'en', visaTypeId) => {
    const params = { lang };
    if (visaTypeId) params.visa_type_id = visaTypeId;
    const response = await api.get('/document-types', { params });
    return response.data?.data?.document_types ?? [];
  }
};

export const resourcesApi = {
  getList: async (lang = 'en', type) => {
    const params = { lang };
    if (type) params.type = type;
    const response = await api.get('/resources', { params });
    return response.data?.data?.resources ?? [];
  },
  getBySlug: async (slug, lang = 'en') => {
    const response = await api.get(`/resources/${encodeURIComponent(slug)}`, { params: { lang } });
    return response.data?.data?.resource ?? null;
  }
};

/** Setări publice (fără auth) – ex: header_top_bar_text pentru bara de sus */
export const settingsPublicApi = {
  get: async () => {
    const response = await api.get('/settings/public');
    return response.data?.data ?? {};
  }
};
